import * as React from 'react'
import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area'
import { cn } from '@/lib/cn'
import { useCardRowItem } from '@/components/layout/card-row-context'

export interface ScrollAreaProps extends React.ComponentPropsWithRef<typeof ScrollAreaPrimitive.Root> {
  /**
   * Ref to the element that actually scrolls (Radix's Viewport). `ref` lands on the Root, which
   * clips but never scrolls — an IntersectionObserver for infinite loading must be rooted on the
   * viewport, so callers need this to reach it.
   */
  viewportRef?: React.Ref<HTMLDivElement>
  /**
   * Classes for the Viewport. A content-sized area capped by a max height (`max-h-80`) needs the
   * cap HERE: the Viewport is `h-full`, which resolves to auto inside an auto-height Root, so a
   * `max-h-*` on the Root alone clips the content instead of scrolling it.
   */
  viewportClassName?: string
  /**
   * Join a flex chain (ADR-024): Root becomes `flex flex-col flex-1 min-h-0` and the Viewport
   * `flex-1 min-h-0` instead of `h-full`. The parent must be a flex column with a definite height or
   * a max height — the area then takes the remaining space and scrolls only when it is cut short.
   */
  fill?: boolean
  /**
   * Item-count cap (PRD M3): the viewport's max height is the measured height of the first `n`
   * items. Items are the element children of the single element passed as `children` (the `<li>`s
   * of a `<ul>`). With `n` items or fewer, no cap is applied.
   */
  maxItems?: number
  /**
   * Item-count floor (PRD M4): the viewport's min height is the measured height of the first
   * `min(n, count)` items — it never adds empty space. Only meaningful in a content-sized container
   * (PRD §2 exception). Defaults to the enclosing `CardRow` follower's `minItems`.
   */
  minItems?: number
  /**
   * Accessible name for the viewport. While the viewport overflows it is a keyboard stop
   * (`tabIndex=0`) and, when `label` is set, a named `role="region"`. Without a label there is no
   * region role — an unnamed region is an axe violation.
   */
  label?: string
}

/** Radix wraps `children` in a `display: table` div: Viewport → wrapper → the caller's element. */
function itemContainerOf(viewport: HTMLElement): Element | null {
  return viewport.firstElementChild?.firstElementChild ?? null
}

/** Height of the first `n` items, gaps and borders between them included. */
function extentOf(items: Element[], n: number): number {
  const k = Math.min(n, items.length)
  if (k <= 0) return 0
  return items[k - 1].getBoundingClientRect().bottom - items[0].getBoundingClientRect().top
}

function assignRef<T>(ref: React.Ref<T> | undefined, value: T | null) {
  if (typeof ref === 'function') ref(value)
  else if (ref) (ref as React.RefObject<T | null>).current = value
}

/**
 * Measures the viewport's items and applies the cap/floor, and tracks whether the viewport
 * overflows. The first measurement is synchronous (no painted frame without the cap or floor);
 * every later one is coalesced into a single `requestAnimationFrame` fed by one ResizeObserver
 * (viewport, item container, each item) and one MutationObserver (`childList`). Scrolling never
 * measures. Returns the overflow state, which changes only when it flips.
 */
function useItemExtent(
  viewportRef: React.RefObject<HTMLDivElement | null>,
  maxItems: number | undefined,
  minItems: number | undefined,
): boolean {
  const [overflowing, setOverflowing] = React.useState(false)
  const caps = React.useRef({ maxItems, minItems })

  const measure = React.useCallback(() => {
    const viewport = viewportRef.current
    if (!viewport) return
    const { maxItems: max, minItems: min } = caps.current
    const container = itemContainerOf(viewport)
    const items = container ? Array.from(container.children) : []

    const maxHeight = max && max > 0 && items.length > max ? `${extentOf(items, max)}px` : ''
    const minHeight = min && min > 0 && items.length > 0 ? `${extentOf(items, min)}px` : ''
    if (viewport.style.maxHeight !== maxHeight) viewport.style.maxHeight = maxHeight
    if (viewport.style.minHeight !== minHeight) viewport.style.minHeight = minHeight

    // PRD FR-4: overflowing = scrollHeight > clientHeight (both integer-rounded by the browser).
    const next = viewport.scrollHeight > viewport.clientHeight
    setOverflowing((prev) => (prev === next ? prev : next))
  }, [viewportRef])

  // Synchronous measurement on mount and whenever the cap or floor changes.
  React.useLayoutEffect(() => {
    caps.current = { maxItems, minItems }
    measure()
  }, [maxItems, minItems, measure])

  React.useLayoutEffect(() => {
    const mounted = viewportRef.current
    if (!mounted) return
    const viewport: HTMLDivElement = mounted

    let frame = 0
    let container: Element | null = null
    const observedItems = new Set<Element>()

    function schedule() {
      if (frame) return
      frame = requestAnimationFrame(() => {
        frame = 0
        track()
        measure()
      })
    }

    const resizeObserver = new ResizeObserver(schedule)
    const mutationObserver = new MutationObserver(schedule)

    // Keep the observers pointed at the current item container and items.
    function track() {
      const next = itemContainerOf(viewport)
      if (next !== container) {
        if (container) resizeObserver.unobserve(container)
        container = next
        if (container) {
          resizeObserver.observe(container)
          mutationObserver.observe(container, { childList: true })
        }
      }
      const items = new Set(container ? Array.from(container.children) : [])
      for (const item of observedItems) {
        if (!items.has(item)) {
          resizeObserver.unobserve(item)
          observedItems.delete(item)
        }
      }
      for (const item of items) {
        if (!observedItems.has(item)) {
          resizeObserver.observe(item)
          observedItems.add(item)
        }
      }
    }

    resizeObserver.observe(viewport)
    // The caller's element itself can be replaced (an empty state swapped for a list).
    if (viewport.firstElementChild) mutationObserver.observe(viewport.firstElementChild, { childList: true })
    track()

    return () => {
      if (frame) cancelAnimationFrame(frame)
      resizeObserver.disconnect()
      mutationObserver.disconnect()
    }
  }, [viewportRef, measure])

  return overflowing
}

const ScrollArea = React.forwardRef<
  React.ComponentRef<typeof ScrollAreaPrimitive.Root>,
  ScrollAreaProps
>(({ className, children, viewportRef, viewportClassName, fill, maxItems, minItems, label, ...props }, ref) => {
  const rowItem = useCardRowItem()
  const floor = minItems ?? rowItem?.minItems
  const innerViewportRef = React.useRef<HTMLDivElement | null>(null)
  const setViewport = React.useCallback(
    (node: HTMLDivElement | null) => {
      innerViewportRef.current = node
      assignRef(viewportRef, node)
    },
    [viewportRef],
  )
  const overflowing = useItemExtent(innerViewportRef, maxItems, floor)

  // A keyboard stop only while there is something to scroll to (NFR-1).
  const a11y = overflowing
    ? { tabIndex: 0, ...(label ? { role: 'region' as const, 'aria-label': label } : {}) }
    : {}

  return (
    <ScrollAreaPrimitive.Root
      ref={ref}
      className={cn('relative overflow-hidden', fill && 'flex min-h-0 flex-1 flex-col', className)}
      {...props}
    >
      <ScrollAreaPrimitive.Viewport
        ref={setViewport}
        data-scroll-viewport=""
        {...a11y}
        className={cn(
          fill ? 'min-h-0 w-full flex-1' : 'h-full w-full',
          'rounded-[inherit]',
          overflowing && 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30',
          viewportClassName,
        )}
      >
        {children}
      </ScrollAreaPrimitive.Viewport>
      <ScrollBar />
      <ScrollAreaPrimitive.Corner />
    </ScrollAreaPrimitive.Root>
  )
})
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName

const ScrollBar = React.forwardRef<
  React.ComponentRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
  React.ComponentPropsWithRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>
>(({ className, orientation = 'vertical', ...props }, ref) => (
  <ScrollAreaPrimitive.ScrollAreaScrollbar
    ref={ref}
    orientation={orientation}
    className={cn(
      'flex touch-none select-none transition-colors',
      orientation === 'vertical' && 'h-full w-2.5 border-l border-l-transparent p-[1px]',
      orientation === 'horizontal' && 'h-2.5 w-full flex-col border-t border-t-transparent p-[1px]',
      className
    )}
    {...props}
  >
    <ScrollAreaPrimitive.ScrollAreaThumb className="relative flex-1 rounded-full bg-border" />
  </ScrollAreaPrimitive.ScrollAreaScrollbar>
))
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName

export { ScrollArea, ScrollBar }
